/**
 * Utilities for handling maintenance task frequency mapping
 * Maps user-friendly frequency selections to backend frequency strings
 */

export interface FrequencySelection {
	every: number;
	interval: 'day' | 'week' | 'month' | 'year';
}

/** Named frequency-string values that carry domain meaning at call sites. */
export const FrequencyValue = {
	Once: 'once',
} as const;

/** The selection a task defaults to the moment recurrence is switched on. */
export const DEFAULT_RECURRING_SELECTION: FrequencySelection = { every: 1, interval: 'month' };

export const FREQUENCY_INTERVALS = [
	{ value: 'day', label: 'Day(s)' },
	{ value: 'week', label: 'Week(s)' },
	{ value: 'month', label: 'Month(s)' },
	{ value: 'year', label: 'Year(s)' },
] as const;

export const PREDEFINED_FREQUENCIES = [
	{ value: FrequencyValue.Once, label: "Doesn't repeat" },
	{ value: 'monthly', label: 'Monthly' },
	{ value: 'quarterly', label: 'Quarterly (Every 3 months)' },
	{ value: '6 months', label: 'Every 6 months' },
	{ value: 'annually', label: 'Yearly' },
] as const;

/**
 * Converts frequency selection to backend frequency string format
 * Handles both predefined frequencies and custom "every N interval" patterns
 */
export const mapFrequencySelectionToString = (selection: FrequencySelection | string): string => {
	// If it's already a string (predefined frequency), return as-is
	if (typeof selection === 'string') {
		return selection;
	}

	const { every, interval } = selection;

	// Handle singular vs plural for readability
	if (every === 1) {
		return `every ${interval}`;
	}

	return `every ${every} ${interval}s`;
};

/** Frequency strings are free text on the API, so compare them in one canonical shape. */
export const normaliseFrequency = (frequency?: string | null): string => frequency?.toLowerCase().trim() ?? '';

export const formatFrequencyLabel = (frequency?: string | null): string => {
	if (!frequency) {
		return 'Not recurring';
	}

	const normalized = normaliseFrequency(frequency);

	if (normalized === FrequencyValue.Once) {
		return "Doesn't repeat";
	}

	const predefined = PREDEFINED_FREQUENCIES.find((item) => item.value === normalized);

	if (predefined) {
		return predefined.label;
	}

	// Said in the largest whole unit it amounts to, keeping the wording it was written in: "every
	// 12 months" is a year, and reading it as months invites a comparison against "annually" that
	// finds a difference which isn't there.
	const parsed = parseFrequencyString(normalized);
	const wording =
		parsed && typeof parsed !== 'string'
			? mapFrequencySelectionToString(canonicaliseSelection(parsed))
			: normalized;

	return `${wording.charAt(0).toUpperCase()}${wording.slice(1)}`;
};

/**
 * The same cadence expressed in its largest whole unit. Twelve months is a year and seven days a
 * week, so a schedule that reads — or compares — differently depending on which spelling reached
 * the API is two schedules as far as anyone using it can tell.
 */
const canonicaliseSelection = (selection: FrequencySelection): FrequencySelection => {
	if (selection.interval === 'month' && selection.every % 12 === 0) {
		return { every: selection.every / 12, interval: 'year' };
	}

	if (selection.interval === 'day' && selection.every % 7 === 0) {
		return { every: selection.every / 7, interval: 'week' };
	}

	return selection;
};

const COMPACT_PREDEFINED_FREQUENCIES: Record<string, string> = {
	monthly: '1 mo',
	quarterly: '3 mo',
	'6 months': '6 mo',
	annually: '1 yr',
};

const COMPACT_INTERVAL_LABELS: Record<FrequencySelection['interval'], string> = {
	day: 'd',
	week: 'wk',
	month: 'mo',
	year: 'yr',
};

/**
 * Compact recurrence metadata for space-constrained cards. The adjacent repeat
 * icon already communicates "every", so only the interval value is displayed.
 */
export const formatCompactFrequencyLabel = (frequency?: string | null): string => {
	if (!frequency) return '—';

	const normalized = frequency.toLowerCase().trim();
	const predefined = COMPACT_PREDEFINED_FREQUENCIES[normalized];
	if (predefined) return predefined;

	const selection = toFrequencySelection(normalized);
	if (selection) {
		return `${selection.every} ${COMPACT_INTERVAL_LABELS[selection.interval]}`;
	}

	return formatFrequencyLabel(frequency);
};

/** The cadence each named frequency stands for, so the repeat controls can display one. */
const PREDEFINED_FREQUENCY_SELECTIONS: Record<string, FrequencySelection> = {
	monthly: { every: 1, interval: 'month' },
	quarterly: { every: 3, interval: 'month' },
	'6 months': { every: 6, interval: 'month' },
	annually: { every: 1, interval: 'year' },
};

/**
 * The cadence a frequency string describes, whichever form it takes. Named frequencies carry no
 * count of their own, so editing one would otherwise fall back to "every 1 month" and quietly
 * misstate how often a quarterly or annual task recurs.
 */
export const toFrequencySelection = (frequency?: string | null): FrequencySelection | null => {
	if (!frequency) return null;

	const parsed = parseFrequencyString(frequency);
	if (parsed && typeof parsed !== 'string') return canonicaliseSelection(parsed);

	return typeof parsed === 'string' ? (PREDEFINED_FREQUENCY_SELECTIONS[parsed] ?? null) : null;
};

/**
 * Whether two frequency strings describe the same cadence. "quarterly" and "every 3 months" are
 * one schedule written two ways, so comparing them as text reports an edit that never happened.
 */
export const isSameFrequency = (left?: string | null, right?: string | null): boolean => {
	if (normaliseFrequency(left) === normaliseFrequency(right)) return true;

	const leftSelection = toFrequencySelection(left);
	const rightSelection = toFrequencySelection(right);

	return (
		!!leftSelection &&
		!!rightSelection &&
		leftSelection.every === rightSelection.every &&
		leftSelection.interval === rightSelection.interval
	);
};

/**
 * Parses a frequency string back to FrequencySelection for editing
 * Returns null if the frequency doesn't match expected patterns
 */
export const parseFrequencyString = (frequency: string): FrequencySelection | string | null => {
	const lowerFreq = frequency.toLowerCase().trim();

	// Check if it's a predefined frequency
	const predefined = PREDEFINED_FREQUENCIES.find((f) => f.value === lowerFreq);
	if (predefined) {
		return predefined.value;
	}

	// Parse "every N interval" patterns
	const match = lowerFreq.match(/^every\s*(\d*)\s*(day|week|month|year)s?$/);
	if (match) {
		const every = match[1] ? parseInt(match[1]) : 1;
		if (!Number.isSafeInteger(every) || every < 1) return null;
		const interval = match[2] as 'day' | 'week' | 'month' | 'year';
		return { every, interval };
	}

	return null;
};

/**
 * Gets the maximum reasonable value for the "every" field based on interval
 * Prevents users from selecting unreasonable values like "every 999 years"
 */
export const getMaxEveryValue = (interval: string): number => {
	switch (interval) {
		case 'day':
			return 365; // Max 1 year in days
		case 'week':
			return 52; // Max 1 year in weeks
		case 'month':
			return 24; // Max 2 years in months
		case 'year':
			return 10; // Max 10 years
		default:
			return 10;
	}
};

/**
 * Values offered by the frequency count select. Keep the ordinary menu short, while retaining one
 * saved legacy value outside that range so opening the editor cannot silently change it.
 */
export const getFrequencyEveryOptions = (selection: FrequencySelection): number[] => {
	const visibleMax = Math.min(getMaxEveryValue(selection.interval), 12);
	const options = Array.from({ length: visibleMax }, (_, index) => index + 1);

	if (Number.isSafeInteger(selection.every) && selection.every > visibleMax) {
		options.push(selection.every);
	}

	return options;
};
